import { formatDate } from "@sembark-travel/datetime-utils"
import type {
	TWAFlow,
	TWAFlowScreen,
	TWATemplate,
	TWhatsappBusinessAccountPhoneNumber,
} from "../Integrations"
import type { IUser } from "../Users"
import type { TWAQuickSend } from "../WhatsAppQuickSends"
import type {
	TAutomationOffsetFormValue,
	TAutomationRule,
	TAutomationRuleGroup,
	TAutomationRuleGroupOperator,
	TAutomationRuleOperator,
	TAutomationSource,
	TAutomationSourceField,
	TAutomationStepReferenceField,
} from "./store"

/**
 * The form-value shapes the automation definition editor works in, and the pure
 * helpers that read, build and summarise them.
 *
 * These live apart from the components that render them on purpose: a module
 * exporting both components and plain functions loses its Fast Refresh
 * boundary, so `DefinitionDialog` and `RuleGroupEditor` export components only
 * and reach in here for everything else.
 */

export type TAutomationBucket = "to" | "cc" | "bcc"
export type TAutomationActionType =
	| "communication"
	| "create_task"
	| "create_trip_plan_request"
	| "send_whatsapp_flow"
	| "send_wa_quick_send"
	| "send_whatsapp_text"
export type TAutomationStepKind = "action" | "wait" | "condition"

export type TAutomationRuleRowFormValue = {
	id: string
	type: "rule"
	field_source?: "source" | "step"
	field_path?: string
	step_key?: string
	step_scope?: "status" | "output" | "meta"
	show_advanced_step_scope?: boolean
	step_field_path?: string
	show_advanced_step_path?: boolean
	operator?: TAutomationRuleOperator
	value: unknown
}

export type TAutomationRuleGroupFormValue = {
	id: string
	type: "group"
	operator: TAutomationRuleGroupOperator
	rules: Array<TAutomationRuleNodeFormValue>
}

export type TAutomationRuleNodeFormValue =
	| TAutomationRuleRowFormValue
	| TAutomationRuleGroupFormValue

export type TAutomationRecipientRowFormValue = {
	id: string
	bucket?: TAutomationBucket
	path?: string
}

export type TAutomationCommunicationChannelsFormValue = {
	whatsapp: boolean
	email: boolean
}

export type TAutomationTaskAssignmentFormValue = {
	mode?: "none" | "static_user" | "field_path"
	user: IUser | null
	field?: string
}

export type TAutomationTaskDueFormValue = {
	enabled?: boolean
	mode?: "relative_to_trigger" | "relative_to_field"
	field?: string
	offset?: TAutomationOffsetFormValue
}

export type TAutomationDefinitionStepFormValue = {
	id: string
	parent_step_id: string | null
	key?: string
	is_key_customized?: boolean
	is_key_editing?: boolean
	type?: TAutomationStepKind
	action_type?: TAutomationActionType
	wait_mode?: "relative_to_trigger" | "relative_to_field"
	wait_field?: string
	wait_offset?: TAutomationOffsetFormValue
	condition_rules?: TAutomationRuleGroupFormValue
	communication_channels?: TAutomationCommunicationChannelsFormValue
	recipients: Array<TAutomationRecipientRowFormValue>
	wa_template: TWATemplate | null
	wa_phone_number: TWhatsappBusinessAccountPhoneNumber | null
	// Send-WhatsApp-Flow action: the picked Flow, for display; `wa_flow_id`
	// is what is saved. The sender is `wa_phone_number`.
	wa_flow?: TWAFlow | null
	wa_flow_id?: string
	wa_flow_cta?: string
	wa_flow_body?: string
	wa_flow_header?: string
	wa_flow_footer?: string
	// The screen the Flow opens on; Meta requires one. Picked from the
	// Flow's own screens, so an object like the other pickers.
	wa_flow_screen?: TWAFlowScreen | null
	// Send-quick-send action: the picked quick send. The sender is
	// `wa_phone_number`.
	wa_quick_send?: Pick<TWAQuickSend, "id" | "name"> | null
	// Send-WhatsApp-text action: the message. The sender is `wa_phone_number`.
	wa_text_body?: string
	trip_source?: string
	task_body_template?: string
	task_assignment?: TAutomationTaskAssignmentFormValue
	task_due?: TAutomationTaskDueFormValue
}

export function createId(prefix: string) {
	return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

export function trimmedString(value: unknown): string {
	return typeof value === "string" ? value.trim() : String(value ?? "").trim()
}

export function createRuleRowFormValue(
	partial?: Partial<TAutomationRuleRowFormValue>
): TAutomationRuleRowFormValue {
	const showAdvancedStepScope =
		partial?.show_advanced_step_scope ?? partial?.step_scope === "meta"

	return {
		id: createId("rule"),
		type: "rule",
		field_source: "source",
		field_path: "",
		step_key: "",
		step_scope: "status",
		show_advanced_step_scope: showAdvancedStepScope,
		step_field_path: "",
		show_advanced_step_path: false,
		operator: "equals",
		value: "",
		...partial,
	}
}

export function createRuleGroupFormValue(
	partial?: Partial<TAutomationRuleGroupFormValue>,
	options?: {
		withInitialRule?: boolean
	}
): TAutomationRuleGroupFormValue {
	const withInitialRule = options?.withInitialRule ?? true

	return {
		id: createId("group"),
		type: "group",
		operator: "and",
		rules: withInitialRule ? [createRuleRowFormValue()] : [],
		...partial,
	}
}

export function isRuleRowNode(
	node: TAutomationRuleNodeFormValue
): node is TAutomationRuleRowFormValue {
	return node.type === "rule"
}

export function formatFieldLabel(
	source: TAutomationSource | null | undefined,
	key?: string | null
) {
	if (!source || !key) {
		return key || "the selected field"
	}

	const field =
		source.filter_fields.find((item) => item.key === key) ||
		source.schedule_fields.find((item) => item.key === key) ||
		source.wait_fields.find((item) => item.key === key) ||
		source.recipient_fields.find((item) => item.key === key) ||
		source.assignable_user_fields.find((item) => item.key === key)

	return field?.label || key
}

export function formatFieldOptionLabel(field: TAutomationSourceField) {
	return `${field.label} (${field.key})`
}

export function fieldTypeLabel(field?: TAutomationSourceField | null) {
	if (!field) {
		return "text"
	}

	return (
		{
			string: "text",
			number: "number",
			boolean: "true/false",
			date: "date",
			datetime: "date & time",
			phone: "phone",
			user: "user",
		}[field.type] || field.type
	)
}

export function fieldValueHint(
	field?: TAutomationSourceField | null,
	isUntypedField = false
) {
	if (isUntypedField) {
		return "Custom fields carry no declared type. Enter the value the way it arrives in the submission."
	}

	if (!field) {
		return "Choose a field to see the expected value format."
	}

	if (field.options?.length) {
		return "Choose one of the available options for this field."
	}

	if (field.type === "date") {
		return "Pick a calendar date. Example: 2025-02-23."
	}

	if (field.type === "datetime") {
		return "Pick the relevant date. Time-based comparisons will use the stored date-time value."
	}

	if (field.type === "number") {
		return "Enter a numeric value only."
	}

	if (field.type === "boolean") {
		return "Choose true or false."
	}

	return "Enter the value in the same format as this field."
}

export function parseRuleReferencePath(fieldPath: string) {
	if (fieldPath.startsWith("steps.")) {
		const [, stepKey, stepScope, ...rest] = fieldPath.split(".")

		if (
			stepKey &&
			(stepScope === "status" || stepScope === "output" || stepScope === "meta")
		) {
			return {
				field_source: "step" as const,
				field_path: "",
				step_key: stepKey,
				step_scope: stepScope,
				step_field_path: rest.join("."),
			}
		}
	}

	if (fieldPath.startsWith("source.")) {
		return {
			field_source: "source" as const,
			field_path: fieldPath.replace(/^source\./, ""),
			step_key: "",
			step_scope: "status" as const,
			step_field_path: "",
		}
	}

	return {
		field_source: "source" as const,
		field_path: fieldPath,
		step_key: "",
		step_scope: "status" as const,
		step_field_path: "",
	}
}

export function buildRuleFieldPath(node: TAutomationRuleRowFormValue) {
	if (node.field_source === "step") {
		if (!node.step_key || !node.step_scope) {
			return ""
		}

		if (node.step_scope === "status") {
			return `steps.${node.step_key}.status`
		}

		return `steps.${node.step_key}.${node.step_scope}${
			trimmedString(node.step_field_path)
				? `.${trimmedString(node.step_field_path)}`
				: ""
		}`
	}

	return trimmedString(node.field_path)
}

export function ruleGroupFormValueFromGroup(
	group?: TAutomationRuleGroup | null
): TAutomationRuleGroupFormValue {
	if (!group?.rules?.length) {
		return createRuleGroupFormValue({}, { withInitialRule: false })
	}

	return createRuleGroupFormValue({
		operator: group.operator || "and",
		rules: group.rules.map((node) =>
			"field_path" in node
				? createRuleRowFormValue({
						...parseRuleReferencePath(node.field_path),
						operator: node.operator,
						value: Array.isArray(node.value)
							? node.value.join(", ")
							: (node.value ?? ""),
					} as TAutomationRuleRowFormValue)
				: ruleGroupFormValueFromGroup(node)
		),
	})
}

export function operatorLabel(operator: TAutomationRuleOperator) {
	return (
		{
			equals: "Equals",
			not_equals: "Does not equal",
			is_null: "Is empty",
			is_not_null: "Is filled",
			contains: "Contains",
			matches_keyword: "Is a keyword",
			contains_keyword: "Has a keyword",
			in: "In list",
			greater_than: "Greater than",
			greater_than_or_equal: "Greater than or equal",
			less_than: "Less than",
			less_than_or_equal: "Less than or equal",
			before: "Before",
			after: "After",
		}[operator] || operator
	)
}

/**
 * Every operator the definition normalizer accepts, in the order the picker
 * offers them. Wildcard fields (`extras_*`) use the whole list: the source
 * declares no type for an ad-hoc submission field, so nothing can be ruled out.
 */
export const ALL_RULE_OPERATORS: Array<TAutomationRuleOperator> = [
	"equals",
	"not_equals",
	"contains",
	"matches_keyword",
	"contains_keyword",
	"in",
	"greater_than",
	"greater_than_or_equal",
	"less_than",
	"less_than_or_equal",
	"before",
	"after",
	"is_null",
	"is_not_null",
]

export function operatorsForField(field?: TAutomationSourceField | null) {
	if (!field) {
		return ["equals", "not_equals"] as Array<TAutomationRuleOperator>
	}

	if (field.type === "boolean") {
		return [
			"equals",
			"not_equals",
			"is_null",
			"is_not_null",
		] as Array<TAutomationRuleOperator>
	}

	if (field.type === "number") {
		return [
			"equals",
			"not_equals",
			"greater_than",
			"greater_than_or_equal",
			"less_than",
			"less_than_or_equal",
			"is_null",
			"is_not_null",
		] as Array<TAutomationRuleOperator>
	}

	// A list-valued field holds several entries, so only the operators that
	// read a list mean anything on it. Equals would compare the whole list
	// against one typed value and never match, silently.
	if (field.type === "string_list") {
		return [
			"contains",
			"is_null",
			"is_not_null",
		] as Array<TAutomationRuleOperator>
	}

	if (field.type === "date" || field.type === "datetime") {
		return [
			"equals",
			"not_equals",
			"before",
			"after",
			"is_null",
			"is_not_null",
		] as Array<TAutomationRuleOperator>
	}

	return [
		"equals",
		"not_equals",
		"contains",
		"matches_keyword",
		"contains_keyword",
		"in",
		"is_null",
		"is_not_null",
	] as Array<TAutomationRuleOperator>
}

/**
 * The operators whose value is a list of keywords rather than one value.
 */
export function isKeywordOperator(
	operator: TAutomationRuleOperator | undefined
) {
	return operator === "matches_keyword" || operator === "contains_keyword"
}

/**
 * Keywords as typed: one per line or between commas, so a phrase keeps its
 * spaces.
 */
export function splitKeywords(value: string): Array<string> {
	return value
		.split(/[\r\n,]+/)
		.map((keyword) => keyword.trim())
		.filter(Boolean)
}

export function ruleRequiresValue(
	operator: TAutomationRuleOperator | undefined
) {
	return operator !== "is_null" && operator !== "is_not_null"
}

export function parseRuleValue(
	field: TAutomationSourceField | undefined,
	operator: TAutomationRuleOperator,
	rawValue: unknown
) {
	if (!ruleRequiresValue(operator)) {
		return undefined
	}

	const value = normalizeRuleValue(rawValue)

	if (isKeywordOperator(operator)) {
		return splitKeywords(value)
	}

	if (operator === "in") {
		return value
			.split(",")
			.map((item) => item.trim())
			.filter(Boolean)
			.map((item) => castScalarValue(field, item))
	}

	return castScalarValue(field, value)
}

export function castScalarValue(
	field: TAutomationSourceField | undefined,
	raw: string
) {
	if (field?.type === "boolean") {
		return raw === "true"
	}

	if (field?.type === "number") {
		return Number(raw)
	}

	return raw
}

export function normalizeRuleValue(value: unknown) {
	if (value === null || value === undefined) {
		return ""
	}

	if (typeof value === "string") {
		return value.trim()
	}

	if (typeof value === "number" || typeof value === "boolean") {
		return String(value)
	}

	if (value instanceof Date) {
		return formatDate(value, "YYYY-MM-DD")
	}

	if (typeof value === "object") {
		if (
			"format" in (value as Record<string, unknown>) &&
			typeof (value as { format?: unknown }).format === "function"
		) {
			try {
				return String(
					(value as { format: (pattern?: string) => string }).format(
						"YYYY-MM-DD"
					)
				)
			} catch {
				return ""
			}
		}

		if (
			"toISOString" in (value as Record<string, unknown>) &&
			typeof (value as { toISOString?: unknown }).toISOString === "function"
		) {
			try {
				return formatDate(
					new Date((value as { toISOString: () => string }).toISOString()),
					"YYYY-MM-DD"
				)
			} catch {
				return ""
			}
		}
	}

	return String(value).trim()
}

export function buildRuleGroup(
	group: TAutomationRuleGroupFormValue,
	fields: Array<TAutomationSourceField>
): TAutomationRuleGroup {
	return {
		operator: group.operator,
		rules: group.rules
			.map((node) => {
				if (isRuleRowNode(node)) {
					const ruleFieldPath = buildRuleFieldPath(node)

					if (!ruleFieldPath || !node.operator) {
						return null
					}

					const field =
						node.field_source === "source"
							? fields.find((item) => item.key === node.field_path)
							: undefined
					const value = parseRuleValue(field, node.operator, node.value)

					return {
						field_path: ruleFieldPath,
						operator: node.operator,
						...(value !== undefined ? { value } : {}),
					} as TAutomationRule
				}

				const nestedGroup = buildRuleGroup(node, fields)

				return nestedGroup.rules.length ? nestedGroup : null
			})
			.filter(
				(node): node is TAutomationRule | TAutomationRuleGroup => node !== null
			),
	}
}

export function validateRuleGroupFormValue(
	group: TAutomationRuleGroupFormValue | undefined,
	basePath: string,
	errors: Record<string, string>
) {
	let hasAtLeastOneRule = false

	if (!group) {
		return hasAtLeastOneRule
	}

	group.rules.forEach((node, index) => {
		const path = `${basePath}.rules[${index}]`

		if (isRuleRowNode(node)) {
			if (!buildRuleFieldPath(node)) {
				return
			}

			hasAtLeastOneRule = true

			if (ruleRequiresValue(node.operator) && !normalizeRuleValue(node.value)) {
				errors[`${path}.value`] = "Enter a value for this rule."
			} else if (
				isKeywordOperator(node.operator) &&
				!splitKeywords(normalizeRuleValue(node.value)).length
			) {
				errors[`${path}.value`] = "Enter at least one keyword."
			}
			return
		}

		if (validateRuleGroupFormValue(node, path, errors)) {
			hasAtLeastOneRule = true
		}
	})

	return hasAtLeastOneRule
}

export function formatRuleReferenceLabel(
	source: TAutomationSource | null | undefined,
	node: TAutomationRuleRowFormValue
) {
	if (node.field_source === "step") {
		const stepFieldPath = String(node.step_field_path || "").trim()

		if (!node.step_key) {
			return "Selected step"
		}

		if (node.step_scope === "status") {
			return `${node.step_key} status`
		}

		return `${node.step_key} ${node.step_scope}${
			stepFieldPath ? `.${stepFieldPath}` : ""
		}`
	}

	return formatFieldLabel(source, node.field_path)
}

export function stepReferenceFieldOptions(
	source: TAutomationSource | null | undefined,
	step: TAutomationDefinitionStepFormValue | undefined,
	scope: "status" | "output" | "meta"
): Array<TAutomationStepReferenceField> {
	if (!source || !step || scope === "status") {
		return []
	}

	if (step.type === "wait") {
		const waitMode = source.supported_steps?.wait?.modes?.find(
			(mode) => mode.key === step.wait_mode
		)

		return scope === "output"
			? waitMode?.known_output_fields || []
			: waitMode?.known_meta_fields || []
	}

	if (step.type === "action") {
		const actionType = source.supported_steps?.action?.types?.find(
			(type) => type.key === step.action_type
		)

		return scope === "output"
			? actionType?.known_output_fields || []
			: actionType?.known_meta_fields || []
	}

	return []
}

export function stepReferenceHint(
	source: TAutomationSource | null | undefined,
	step: TAutomationDefinitionStepFormValue | undefined,
	scope: "status" | "output" | "meta"
) {
	if (!step) {
		return "Choose a previous step to see its available values."
	}

	if (scope === "status") {
		return "Use the previous step status such as completed or failed."
	}

	const options = stepReferenceFieldOptions(source, step, scope)

	if (options.length) {
		return "Pick one of the known fields for this step. Use Advanced only if you need a custom nested path."
	}

	return "This step does not expose curated fields here yet. Use Advanced to enter a custom nested path if needed."
}

export function summarizeRuleGroup(group: TAutomationRuleGroupFormValue) {
	const nonEmptyChildren = group.rules.filter((node) =>
		isRuleRowNode(node)
			? Boolean(buildRuleFieldPath(node))
			: node.rules.some((child) =>
					isRuleRowNode(child) ? Boolean(buildRuleFieldPath(child)) : true
				)
	)

	if (!nonEmptyChildren.length) {
		return "No rules added yet."
	}

	return `${nonEmptyChildren.length} condition${nonEmptyChildren.length === 1 ? "" : "s"} matched by ${group.operator === "and" ? "all" : "any"} rule${nonEmptyChildren.length === 1 ? "" : "s"}.`
}

export function summarizeRuleNode(
	source: TAutomationSource | null | undefined,
	node: TAutomationRuleNodeFormValue
): string {
	if (isRuleRowNode(node)) {
		if (!buildRuleFieldPath(node)) {
			return "Choose a field to define this rule."
		}

		const fieldLabel = formatRuleReferenceLabel(source, node)
		const operatorText = node.operator
			? operatorLabel(node.operator).toLowerCase()
			: ""

		if (!ruleRequiresValue(node.operator)) {
			return `${fieldLabel} ${operatorText}`
		}

		const value = formatRuleValueForSummary(node.value)
		return `${fieldLabel} ${operatorText} ${value}`
	}

	return summarizeRules(source, node)
}

export function summarizeRules(
	source: TAutomationSource | null | undefined,
	group: TAutomationRuleGroupFormValue
): string {
	const activeRules = group.rules
		.map((node) => summarizeRuleNode(source, node))
		.filter((text) => text !== "Choose a field to define this rule.")

	if (!activeRules.length) {
		return "This group is currently empty."
	}

	const joiner = group.operator === "and" ? " AND " : " OR "
	return activeRules.length === 1
		? activeRules[0]
		: `(${activeRules.join(joiner)})`
}

export function formatRuleValueForSummary(value: unknown) {
	if (value === null || value === undefined || value === "") {
		return "?"
	}

	if (typeof value === "string") {
		return value.trim() || "?"
	}

	if (typeof value === "number" || typeof value === "boolean") {
		return String(value)
	}

	if (value instanceof Date) {
		return formatDate(value, "YYYY-MM-DD")
	}

	if (typeof value === "object") {
		if (
			"format" in (value as Record<string, unknown>) &&
			typeof (value as { format?: unknown }).format === "function"
		) {
			try {
				return String(
					(value as { format: (pattern?: string) => string }).format(
						"YYYY-MM-DD"
					)
				)
			} catch {
				return String(value)
			}
		}

		if (
			"toISOString" in (value as Record<string, unknown>) &&
			typeof (value as { toISOString?: unknown }).toISOString === "function"
		) {
			try {
				return formatDate(
					new Date((value as { toISOString: () => string }).toISOString()),
					"YYYY-MM-DD"
				)
			} catch {
				return String(value)
			}
		}
	}

	return String(value)
}
